import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ListQuery } from "../../core";
import { useRuntime } from "../runtime/RuntimeContext";

// Loads an action's metadata + view plans by slug. Cached per slug — the plan is
// stable for a session, so a stale window avoids refetching on every navigation.
export function useActionLoad(slug: string) {
    const runtime = useRuntime();
    return useQuery({
        queryKey: ["action", slug],
        queryFn: () => runtime.services.action.loadAction(slug),
        enabled: Boolean(slug),
        staleTime: 5 * 60 * 1000,
    });
}

// Lists records for an action. keepPreviousData keeps the current rows on screen
// while a new page/filter loads, so paging doesn't flash empty.
export function useActionRecords(slug: string, query: ListQuery, enabled = true) {
    const runtime = useRuntime();
    return useQuery({
        queryKey: ["action-records", slug, query],
        queryFn: () => runtime.services.action.listRecords(slug, query),
        enabled: enabled && Boolean(slug),
        placeholderData: keepPreviousData,
    });
}

// One record for the (dummy) form view / breadcrumb. Disabled for the "new" ref.
export function useRecord(slug: string, recordRef: string | undefined, viewType = "form") {
    const runtime = useRuntime();
    return useQuery({
        queryKey: ["record", slug, recordRef, viewType],
        queryFn: () => runtime.services.action.readRecord(slug, recordRef as string, viewType),
        enabled: Boolean(slug) && Boolean(recordRef) && recordRef !== "new",
    });
}

// All possible values of a reference group-by field (kanban columns, incl. empty
// stages) — sourced from the related-records endpoint.
export function useGroupOptions(slug: string, field: string, enabled: boolean) {
    const runtime = useRuntime();
    return useQuery({
        queryKey: ["group-options", slug, field],
        queryFn: () => runtime.services.action.searchRelated(slug, field, ""),
        enabled: enabled && Boolean(slug) && Boolean(field),
        staleTime: 60 * 1000,
    });
}

// Predefined + user favourite saved filters for an action.
export function useActionFilters(slug: string) {
    const runtime = useRuntime();
    return useQuery({
        queryKey: ["filters", slug],
        queryFn: () => runtime.services.action.listFilters(slug),
        enabled: Boolean(slug),
        staleTime: 60 * 1000,
    });
}
